import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { toast } from "react-toastify";
import axios from "../../api/axios_client";
import './ManagementPages.css';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

const AreasManagement = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    byCountry: {}
  });

  const countries = [
    "Algeria", "Bahrain", "Comoros", "Djibouti", "Egypt", "Iraq", "Jordan",
    "Kuwait", "Lebanon", "Libya", "Mauritania", "Morocco", "Oman", "Palestine",
    "Qatar", "Saudi Arabia", "Somalia", "Sudan", "Syria", "Tunisia",
    "United Arab Emirates", "Yemen"
  ];

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const params = { tab: 'areas' };
      if (selectedCountry) {
        params.country = selectedCountry;
      }
      
      const response = await axios.get('/admin-panel', { params });
      setAreas(response.data);
      
      // Calculate stats
      const stats = {
        total: response.data.length,
        byCountry: {}
      };

      response.data.forEach(area => {
        stats.byCountry[area.country] = (stats.byCountry[area.country] || 0) + 1;
      });

      setStats(stats);
    } catch (error) {
      toast.error('Failed to fetch areas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (area) => {
    setAreaToDelete(area);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`/areas/${areaToDelete.id}`);
      toast.success('Area deleted successfully');
      fetchAreas();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete area';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setAreaToDelete(null);
    }
  };

  const handleApprovalToggle = (area) => {
    // Placeholder for future implementation
    toast.info(`Approval toggle for ${area.name} - Coming soon!`);
  };

  useEffect(() => {
    fetchAreas();
  }, [selectedCountry]);

  const filteredAreas = areas.filter(area => 
    (!searchTerm || area.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const metrics = [
    {
      title: "Total Areas",
      value: stats.total,
      change: "+4.3%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
      ),
    },
    {
      title: "Countries Coverage",
      value: Object.keys(stats.byCountry).length,
      change: "+2.8%",
      trend: "up",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="metric-icon"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      ),
    },
    // {
    //   title: "Users with Areas",
    //   value: stats.total,
    //   change: "+1.9%",
    //   trend: "up",
    //   icon: (
    //     <svg
    //       xmlns="http://www.w3.org/2000/svg"
    //       viewBox="0 0 24 24"
    //       fill="none"
    //       stroke="currentColor"
    //       strokeWidth="2"
    //       strokeLinecap="round"
    //       strokeLinejoin="round"
    //       className="metric-icon"
    //     >
    //       <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
    //       <circle cx="9" cy="7" r="4"></circle>
    //       <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
    //       <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
    //     </svg>
    //   ),
    // },
  ];

  return (
    <div className="management-page">
      <div className="management-header">
        <h1>Areas Management</h1>
        <p>Manage and monitor all geographical areas across the platform</p>
      </div>

      <div className="metrics-grid">
        {metrics.map((metric, index) => (
          <div key={index} className="metric-card">
            <div className="metric-icon-wrapper">
              {metric.icon}
            </div>
            <div className="metric-content">
              <h3>{metric.title}</h3>
              <div className="number">{metric.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="filters-section">
        <Box display="flex" gap={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Country</InputLabel>
            <Select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              label="Filter by Country"
            >
              <MenuItem value="">All Countries</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country} value={country}>{country}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            placeholder="Search areas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
            }}
          />
        </Box>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="items-grid">
          {filteredAreas.map((area) => (
            <Card key={area.id} className="admin-item-card">
              <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 0 } }}>
                <Box sx={{ p: 2.5, flex: 1 }}>
                  {/* Header with name */}
                  <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                    {area.name}
                  </Typography>

                  {/* Country and description */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>Country:</strong> {area.country}
                    </Typography>
                    {area.description && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        <strong>Description:</strong> {area.description}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: '1px solid #eee' }} />

                {/* Bottom section - aligned to bottom */}
                <Box sx={{ p: 2.5, pt: 2 }}>
                  {/* Created date and user */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDate(area.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        User
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {area.user || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color={area.approved ? 'warning' : 'success'}
                      startIcon={area.approved ? <CancelIcon /> : <CheckCircleIcon />}
                      onClick={() => handleApprovalToggle(area)}
                      sx={{ minWidth: 100 }}
                    >
                      {area.approved ? 'Disapprove' : 'Approve'}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(area)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteDialogOpen(false);
            setAreaToDelete(null);
          }
        }}
        onConfirm={confirmDelete}
        itemName={areaToDelete?.name || 'this area'}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default AreasManagement;